import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import {
  PatronTypeSerializer,
  PublicBaseEventSerializer,
  TicketTypeSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import {
  BasketPreviewProps,
  ticketTypeToBasketPreviewTransform,
} from "@app-contexts/public/features/tickets/components/BasketPreview/BasketPreview.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import {
  useWizardContext,
  WizardProvider,
} from "@shared/contexts/WizardContext/WizardContext";
import { X_SP_UUID } from "@shared/env";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { useWizard } from "@shared/hooks/useWizard/useWizard";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import useAnalyticsHelpers from "@shared/modules/analytics/hooks/useAnalyticsHelpers";
import type { TicketBasket } from "@shared/modules/basket/types";
import { PublicCalendarEventDetailsSerializer } from "@shared/modules/events/types/public";
import {
  isTicketTypeSoldOut,
  isTicketTypeWaitlisted,
} from "@shared/modules/events/utils/ticket-type-utils";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import { useCallback, useMemo, useState } from "react";
import { EventWizardContentProps } from "./EventWizard.props";
import EventCalendarScreen from "./screens/EventCalendarScreen/EventCalendarScreen.web";
import PatronTicketTypeSelectionScreen from "./screens/PatronTicketTypeSelectionScreen/PatronTicketTypeSelectionScreen.web";
import TicketQuantityPromptScreen from "./screens/TicketQuantityPromptScreen/TicketQuantityPromptScreen.web";
import { EVENT_WIZARD_SCREENS_MAP } from "./screens/types";

export type EventWizardProps = {
  // props specific to just the EventWizard
} & EventWizardContentProps;

/**
 * A multi step wizard for selecting event tickets.
 * Handles things like quantity-first prompts, and Attraction parent ticket type selection.
 *
 * Displays a "modal-like" UI for the footer to allow for navigation.
 */
export function EventWizard({ ...rest }: EventWizardProps) {
  const attractionEventWizard = useWizard({
    name: "attraction-events",
  });

  return (
    <WizardProvider wizard={attractionEventWizard}>
      <EventWizardContent
        onSuccess={() => {
          attractionEventWizard.nextStep();
        }}
        {...rest}
      />
    </WizardProvider>
  );
}

export function EventWizardContent({
  venue,
  event,
  promptForQuantity,
  ticketTypeSelectionRequired,
  onSuccess,
  onDismiss,
  isLoading = false,
  holdDetailsData,
  calendarProps,
  onEventSelectCallback,
  onCustomPackageSelect,
}: EventWizardContentProps) {
  const { trackItemsInCart } = useAnalyticsHelpers();
  const { steps, currentStep, goToStep } = useWizardContext();
  const { t } = useTranslation("common");

  const { SWITCHES } = useDjangoWaffle();

  // TODO: Remove after angular sdk is deprecated
  const hideFooter =
    typeof window !== "undefined" &&
    window.location?.search?.includes?.("hide-footer=true");

  const isReservedCountEnabled =
    Boolean(SWITCHES?.enable_reserved_count) &&
    Boolean((venue ?? event?.venue)?.enable_reserve_count_management);
  const canPromptForQuantity =
    Boolean(promptForQuantity) && isReservedCountEnabled;
  const canSelectTicketTypes =
    Boolean(ticketTypeSelectionRequired) && isReservedCountEnabled;

  const getInitialStep = useCallback(() => {
    return steps[0].id;
  }, [steps]);

  const { modifyItems, isBasketUpdating, totalItemQuantity } =
    usePublicBasket();

  const [selectedTicketQuantity, setSelectedTicketQuantity] = useState<
    number | undefined
  >();
  const [selectedTicketTypesAndQuantity, setSelectedTicketTypesAndQuantity] =
    useState<
      Record<
        string,
        {
          name: string;
          quantity: number;
          tt: TicketTypeSerializer;
        }
      >
    >();
  const [selectedEvent, setSelectedEvent] =
    useState<PublicCalendarEventDetailsSerializer | null>();

  const { selectedPatronTypes, selectedTicketTypes } = useMemo(() => {
    if (!selectedTicketTypesAndQuantity) {
      return { selectedPatronTypes: {}, selectedTicketTypes: {} };
    }

    const patronTypeIdMap: Record<string, PatronTypeSerializer> = {};
    const ticketTypeIdMap: Record<number, TicketTypeSerializer> = {};

    Object.values(selectedTicketTypesAndQuantity).forEach((selection) => {
      if (selection.tt.patron_type?.id) {
        patronTypeIdMap[selection.tt.patron_type.id] = selection.tt.patron_type;
      }
      if (selection.tt.id) {
        ticketTypeIdMap[selection.tt.id] = selection.tt;
      }
    });

    return {
      selectedPatronTypes: patronTypeIdMap,
      selectedTicketTypes: ticketTypeIdMap,
    };
  }, [selectedTicketTypesAndQuantity]);

  const handleAddToBasketAnalytics = useCallback(
    (
      basket: TicketBasket,
      event: PublicBaseEventSerializer,
      tt: TicketTypeSerializer,
      quantity: number
    ) => {
      // Perform analytics in isolation; Allow quantity mechanisms to continue
      try {
        const spAddToCartCall = () => {
          logAppInfo("spAddToCartCall");
          UserInteractionLogger.addToCart({
            item: {
              item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
              type: tt.id,
            },
            count: quantity,
            venue: event.venue?.name,
            event,
            basket,
          });
        };
        logAppInfo(`calling trackItemsInCart ${basket?.id}`);
        trackItemsInCart(
          quantity,
          basket,
          {
            itemId: tt.id,
            itemVariant: tt.name,
            itemName: event.name,
            itemType: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
          },
          venue,
          spAddToCartCall,
          { uuid: basket[X_SP_UUID] as string }
        );
      } catch (e) {
        logAppInfo(`Returned basket truthiness: ${Boolean(basket)}`);
        recordAppError(e);
      }
    },
    [trackItemsInCart, venue]
  );

  const handleAddToBasket = useCallback(() => {
    // Select the event's ticket types based on the requested TTs
    const selectedTTs: Parameters<typeof modifyItems>[0] = [];
    selectedEvent?.ticket_types?.forEach((tt) => {
      // find the matching TT by patron_type id and patron_option id.
      // this key should match with how the caller of selectedTicketTypesAndQuantity sets keys.
      const ttKey = tt.patron_type?.id
        ? `${tt.patron_type.id}-${tt.patron_option_id}`
        : tt.name;

      const requestedTT = selectedTicketTypesAndQuantity?.[ttKey];
      if (requestedTT) {
        selectedTTs.push({
          item: tt,
          quantity: requestedTT.quantity,
        });
      }
    });

    // Determine waitlisted TTs and adjust accordingly
    const waitlistedTTs = selectedTTs.filter(
      (tt) => isTicketTypeWaitlisted(tt.item) && isTicketTypeSoldOut(tt.item)
    );

    modifyItems(selectedTTs, {
      is_waitlist: Boolean(waitlistedTTs.length),
      hold_slug: holdDetailsData?.on_hold_detail?.tracking_link_slug,
    })
      .then((returnedBasket) => {
        if (returnedBasket) {
          selectedTTs.forEach((tt) => {
            handleAddToBasketAnalytics(
              returnedBasket,
              selectedEvent as PublicBaseEventSerializer,
              // the type of item from selectedTT has an unknown type
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              tt.item,
              tt.quantity
            );
          });
        }
      })
      .then(() => {
        onSuccess?.();

        setSelectedEvent(undefined);
        setSelectedTicketQuantity(undefined);
        setSelectedTicketTypesAndQuantity(undefined);
      });
  }, [
    selectedTicketTypesAndQuantity,
    selectedEvent,
    // modifyItems dependencies
    modifyItems,
    holdDetailsData,
    handleAddToBasketAnalytics,
    onSuccess,
    getInitialStep,
    goToStep,
  ]);

  const basketPreviewData: BasketPreviewProps = useMemo(() => {
    const previewItems: BasketPreviewProps["items"] = [];

    selectedEvent?.ticket_types?.forEach((tt) => {
      const ttKey = tt.patron_type?.id
        ? `${tt.patron_type.id}-${tt.patron_option_id}`
        : tt.name;

      // Find the selected event's TT in the preselected ticket types and quantities
      const preSelectedTT = selectedTicketTypesAndQuantity?.[ttKey];

      if (preSelectedTT) {
        // Transform into basket preview shape and append

        const PREVIEW_PURCHASE_PLATFORM =
          enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id;
        const PREVIEW_PAYMENT_TYPE = PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id;
        const ticketType = tt;

        // Construct a fees_pricing_info object that fits the preview object here.
        // This is because there are cases where the ticket type may not have a full fees_pricing_info object, such as
        // when it comes from the /calendar endpoints (PublicCalendarEventDetailsSerializer)
        if (
          !ticketType.fees_pricing_info?.[PREVIEW_PURCHASE_PLATFORM]?.[
            PREVIEW_PAYMENT_TYPE
          ]
        ) {
          ticketType.fees_pricing_info = {
            [PREVIEW_PURCHASE_PLATFORM]: {
              // We're doing a data hydration here by making this data follow our standard Event shape, so this will error.
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              [PREVIEW_PAYMENT_TYPE]: {
                ...tt.fees_pricing_info,
              },
            },
          };
        }

        const transformedTicketType = ticketTypeToBasketPreviewTransform(
          ticketType,
          preSelectedTT.quantity
        );

        previewItems.push(transformedTicketType);
      }
    });

    return {
      items: previewItems,
      currency: event?.currency || event?.venue?.currency,
    };
  }, [event, selectedTicketTypesAndQuantity, selectedEvent]);

  return (
    <>
      {promptForQuantity && (
        <TicketQuantityPromptScreen
          event={event}
          isLoading={isLoading}
          onSuccessAction={(quantity) => {
            setSelectedTicketQuantity(quantity);
            goToStep(EVENT_WIZARD_SCREENS_MAP.CALENDAR.id);
          }}
        />
      )}
      {ticketTypeSelectionRequired && (
        <PatronTicketTypeSelectionScreen
          event={event}
          isLoading={isLoading}
          initialSelectedTicketTypes={selectedTicketTypesAndQuantity}
          onDismiss={onDismiss}
          onSuccess={(selectedTTs) => {
            setSelectedTicketTypesAndQuantity(selectedTTs);
            goToStep(EVENT_WIZARD_SCREENS_MAP.CALENDAR.id);
          }}
          eventData={event}
        />
      )}
      <EventCalendarScreen
        {...calendarProps}
        event={event}
        eventTimezone={event?.timezone || venue?.timezone}
        patronTypes={Object.keys(selectedPatronTypes)}
        ticketTypes={Object.keys(selectedTicketTypes)}
        hideFooter={hideFooter}
        footerProps={{
          isLoading: isBasketUpdating,
          disabled: !selectedEvent,
          continueButton: !canSelectTicketTypes ? (
            <Button
              data-testid="event-wizard-continue"
              variant="solid"
              colorPalette="primary"
              isLoading={isBasketUpdating}
              disabled={totalItemQuantity === 0 || isBasketUpdating}
              onClick={onSuccess}
            >
              {t("Continue")}
            </Button>
          ) : (
            <Button
              data-testid="event-wizard-continue"
              variant="solid"
              colorPalette="primary"
              isLoading={isBasketUpdating}
              disabled={!selectedEvent || isBasketUpdating}
              onClick={handleAddToBasket}
            >
              {t("Continue")}
            </Button>
          ),
        }}
        onDismiss={() => {
          const initialStepId = getInitialStep();

          if (initialStepId === currentStep?.id) {
            onDismiss?.();
          } else {
            goToStep(initialStepId);
          }
        }}
        showQuantity={canPromptForQuantity}
        showBasketPreview={canSelectTicketTypes}
        basketPreviewData={basketPreviewData}
        showTicketTypeSelection={!canSelectTicketTypes}
        quantity={
          selectedTicketQuantity ??
          Object.values(selectedTicketTypesAndQuantity || {}).reduce(
            (acc, curr) => {
              let accCopy = acc;
              accCopy += curr.quantity;
              return accCopy;
            },
            0
          )
        }
        onSelectedEvent={(event) => {
          setSelectedEvent(event);
        }}
        onEventSelectCallback={onEventSelectCallback}
        onCustomPackageSelect={onCustomPackageSelect}
      />
    </>
  );
}
